import ApplicationSwitcherMain from './ApplicationSwitcherMain';
import ApplicationSwitcherClient from './ApplicationSwitcherClient';
import ApplicationSwitcherConfig from './ApplicationSwitcherConfig';
import { ApplicationNode, PublicType } from './model/ApplicationSwitcherModel';
import { OPEN, CREATE, SHOW } from './events/ApplicationSwitcherObservable';
import { enableAppSwitcher } from './ApplicationSwitcherStatus';
import { eventBus } from '../internal';
import { beforeTimeoutEvent } from '../eventBus/appEventTypes';
import AppRegistryService from './services/AppRegistryService';

interface ApplicationSwitcherInterface {
  broadcastMessage: (type: string, data: object) => void;
  createObject: (objectType: string, objectInfo: any, preferredAppId?: string) => void;
  gotoApp: (appId: string, url: string, isCompliant: boolean, options?: object) => void;
  logoff: () => void;
  openObject: (objectType: string, objectInfo: any, preferredAppId?: string) => void;
  registerApp: (appId: string, url: string, appName: string) => void;
  sendMessageToApp: (appId: string, type: string, data: any) => void;
  setLogoffUrl: (logoffUrl: string) => void;
  subscribe: (type: string, fn: (type: string, data?: object) => void, context?: object) => void;
  timeout: (url: string) => void;
  unsubscribe: (type: string, fn: (type: string, data?: object) => void, context?: object) => void;
  updateAppName: (appId: string, appName: string) => void;
  updateDirtyFlag: (appId: string, dirtyFlag: boolean) => void;
  updateModel: () => void;
}

class ApplicationSwitcher implements ApplicationSwitcherInterface {
  private client: ApplicationSwitcherClient;

  constructor(private config: ApplicationSwitcherConfig) {
    if (window === top?.window) {
      new ApplicationSwitcherMain(config);
    }
    this.client = new ApplicationSwitcherClient(config);
  }

  broadcastMessage(type: string, data: object = {}) {
    this.client.broadcastMessage(type, data);
  }

  getAvailableApps(): ApplicationNode[] {
    return this.client.getModel().availableApplications;
  }

  getPublicTypes(): Map<string, PublicType> {
    return this.client.getModel().publicTypeSupportingApplications;
  }

  gotoApp(appId: string, url: string, isCompliant: boolean, options?: object) {
    this.client.gotoApp(appId, url, isCompliant, options);
  }

  logoff() {
    this.client.logoff();
  }

  sendMessageToApp(appId: string, type: string, data?: any) {
    this.client.sendMessageToApp(appId, type, data);
  }

  setDirtyMessageEnabled(allowDirtyMessage: boolean) {
    this.client.setDirtyMessageEnabled(allowDirtyMessage);
  }

  setLogoffUrl(logoffUrl: string) {
    this.client.setLogoffUrl(logoffUrl);
  }

  subscribe(type: string, fn: (type: string, data?: object) => void, context?: object) {
    this.client.subscribe(type, fn, context);
  }

  timeout(url: string) {
    this.client.timeout(url);
  }

  unsubscribe(type: string, fn: (type: string, data?: object) => void, context?: object) {
    this.client.unsubscribe(type, fn, context);
  }

  updateAppName(appId: string, appName: string) {
    this.client.updateAppName(appId, appName);
  }

  updateDirtyFlag(appId: string, dirtyFlag: boolean) {
    this.client.updateDirtyFlag(appId, dirtyFlag);
  }

  registerApp(appId: string, url: string, appName: string) {
    this.client.registerApp(appId, url, appName);
  }

  openObject(objectType: string, objectInfo: any, preferredAppId?: string) {
    this.client.openObject(objectType, objectInfo, OPEN, preferredAppId);
  }

  createObject(objectType: string, objectInfo: any, preferredAppId?: string) {
    this.client.openObject(objectType, objectInfo, CREATE, preferredAppId);
  }

  updateModel() {
    this.client.updateModel();
  }

  setUnsavedChangesLocalizedFn(fn: () => string) {
    this.config.getUnsavedChangesLocalizedString = fn;
  }

  getAvailableApp(appId: string) {
    return this.client.getAvailableApp(appId);
  }
}

export const createApplicationSwitcher = (
  config: ApplicationSwitcherConfig
): ApplicationSwitcher => {
  const applicationSwitcher = new ApplicationSwitcher(config);
  enableAppSwitcher();

  // fire up show event on event bus.
  applicationSwitcher.subscribe(SHOW, (_: string, data?: object) => {
    eventBus.publish(SHOW, { data });
  });
  // TODO fire other app switcher events on the bus. Rob?
  eventBus.subscribe(beforeTimeoutEvent, () => {
    applicationSwitcher.setDirtyMessageEnabled(false);
  });

  return applicationSwitcher;
};

const getLogoffUrl = (): string => {
  const origin = window.location.origin;
  const pathname = window.location.pathname;
  const secondSlashIndex = pathname.indexOf('/', 1);
  const contextPath = pathname.substring(0, secondSlashIndex);
  const logoutPath = '/logout';
  return origin + contextPath + logoutPath;
};

let applicationSwitcher: ApplicationSwitcher;
export const getApplicationSwitcher = () => {
  if (!applicationSwitcher) {
    applicationSwitcher = createApplicationSwitcher({
      applicationService: new AppRegistryService(),
      getLogoffUrl,
      getUnsavedChangesLocalizedString: () => {
        return '';
      },
    });
  }

  return applicationSwitcher;
};

export const setApplicationSwitcher = (customApplicationSwitcher: ApplicationSwitcher) => {
  applicationSwitcher = customApplicationSwitcher;
};

export default ApplicationSwitcher;
